import type { IOnboardingContext } from '../types/IOnboardingContext'

/*
 * Зарегистрированные контексты живут вне Pinia: в них функции и DOM-ссылки,
 * которым незачем быть реактивными. Регистрируются через useOnboardingContext().
 */
const contexts = new Map<string, IOnboardingContext>()
const waiters = new Map<string, Array<(context: IOnboardingContext) => void>>()

export function registerContext(context: IOnboardingContext): void {
  contexts.set(context.name, context)

  const pending = waiters.get(context.name) ?? []
  waiters.delete(context.name)
  pending.forEach(resolve => resolve(context))
}

export function unregisterContext(context: IOnboardingContext): void {
  if (contexts.get(context.name) === context)
    contexts.delete(context.name)
}

export function getContext(name: string): IOnboardingContext | undefined {
  return contexts.get(name)
}

/** Ждёт регистрации контекста: форма или страница может смонтироваться после действия входа */
export function waitForContext(name: string, timeoutMs: number): Promise<IOnboardingContext | null> {
  const existing = contexts.get(name)
  if (existing)
    return Promise.resolve(existing)

  return new Promise(resolve => {
    const onRegister = (context: IOnboardingContext) => {
      window.clearTimeout(timer)
      resolve(context)
    }

    const timer = window.setTimeout(() => {
      const list = waiters.get(name) ?? []
      waiters.set(name, list.filter(waiter => waiter !== onRegister))
      resolve(null)
    }, timeoutMs)

    waiters.set(name, [...(waiters.get(name) ?? []), onRegister])
  })
}

/** Выполняет действие контекста; отсутствие контекста или действия — не ошибка онбординга */
export async function runContextAction(contextName: string, action: string): Promise<boolean> {
  const handler = contexts.get(contextName)?.actions[action]
  if (!handler) {
    console.warn(`[onboarding] Нет действия «${action}» в контексте «${contextName}»`)

    return false
  }

  await handler()

  return true
}
